/**
 * Video Grid
 * - Click-to-play for native Shopify-hosted videos
 * - Click-to-play iframe injection for YouTube / Vimeo
 * - YouTube thumbnail quality upgrader (hqdefault → sddefault → maxresdefault)
 * - Vimeo thumbnail fetch via public oEmbed API
 */

(function () {
  'use strict';

  /* -------------------------------------------------- */
  /* Thumbnail upgrading                                 */
  /* -------------------------------------------------- */

  /**
   * YouTube returns a 120×90 black placeholder when maxresdefault
   * doesn't exist. We detect that and step down gracefully.
   * Chain: maxresdefault (1280×720) → sddefault (640×480) → keep hqdefault
   */
  function upgradeYoutubeThumbnail(img) {
    var id = img.dataset.videoId;
    if (!id) return;

    var qualities = ['maxresdefault', 'sddefault'];
    var idx = 0;

    function tryNext() {
      if (idx >= qualities.length) return; // nothing better available — keep hqdefault
      var quality = qualities[idx];
      idx++;

      var probe = new Image();
      probe.onload = function () {
        // YouTube sends a 120×90 blank placeholder when the quality doesn't exist
        if (probe.naturalWidth > 120) {
          img.src = 'https://img.youtube.com/vi/' + id + '/' + quality + '.jpg';
        } else {
          tryNext();
        }
      };
      probe.onerror = function () {
        tryNext();
      };
      probe.src = 'https://img.youtube.com/vi/' + id + '/' + quality + '.jpg';
    }

    tryNext();
  }

  /**
   * Vimeo oEmbed API returns a reliable, high-quality thumbnail URL.
   * The API is public and CORS-friendly.
   */
  function fetchVimeoThumbnail(img) {
    var id = img.dataset.videoId;
    if (!id) return;

    fetch('https://vimeo.com/api/oembed.json?url=https%3A%2F%2Fvimeo.com%2F' + id + '&width=1280')
      .then(function (res) {
        if (!res.ok) throw new Error('oEmbed failed');
        return res.json();
      })
      .then(function (data) {
        if (data.thumbnail_url) {
          // Vimeo oEmbed returns e.g. …_640.jpg — ask for a larger size
          var hdUrl = data.thumbnail_url.replace(/_\d+\.jpg$/, '_1280.jpg');
          img.src = hdUrl;
          img.removeAttribute('hidden');
        }
      })
      .catch(function () {
        // oEmbed failed — hide the broken img so the dark card background shows
        img.style.display = 'none';
      });
  }

  function upgradeAllThumbnails(section) {
    section.querySelectorAll('[data-upgrade-thumb="youtube"]').forEach(upgradeYoutubeThumbnail);
    section.querySelectorAll('[data-upgrade-thumb="vimeo"]').forEach(fetchVimeoThumbnail);
  }

  /* -------------------------------------------------- */
  /* Click-to-play: native <video>                       */
  /* -------------------------------------------------- */

  function initNativePlayers(section) {
    section.querySelectorAll('.video-grid-media--native').forEach(function (media) {
      var playBtn = media.querySelector('.video-grid-play-btn');
      if (!playBtn) return;

      playBtn.addEventListener('click', function () {
        var wrapper = media.querySelector('.video-grid-native-wrapper');
        if (!wrapper) return;

        // Reveal the <video> element
        wrapper.removeAttribute('hidden');

        // Attempt autoplay (attribute is already set in the markup)
        var video = wrapper.querySelector('video');
        if (video) {
          video.play().catch(function () {
            // Autoplay blocked — video is visible with controls, user can press play manually
          });
        }

        // Hide thumbnail and play button overlay
        var coverImg = media.querySelector('.video-grid-cover-img');
        if (coverImg) coverImg.style.display = 'none';
        playBtn.style.display = 'none';
      });
    });
  }

  /* -------------------------------------------------- */
  /* Click-to-play: YouTube / Vimeo iframe               */
  /* -------------------------------------------------- */

  function initEmbedPlayers(section) {
    section.querySelectorAll('.video-grid-media--embed').forEach(function (media) {
      var playBtn = media.querySelector('.video-grid-play-btn');
      if (!playBtn) return;

      playBtn.addEventListener('click', function () {
        loadEmbed(media);
      });
    });
  }

  function loadEmbed(media) {
    var type = media.dataset.videoType;
    var id = media.dataset.videoId;
    if (!type || !id) return;

    var wrapper = media.querySelector('.video-grid-iframe-wrapper');
    if (!wrapper) return;

    var src = '';

    if (type === 'youtube') {
      src =
        'https://www.youtube.com/embed/' +
        id +
        '?autoplay=1&rel=0&modestbranding=1&playsinline=1';
    } else if (type === 'vimeo') {
      src =
        'https://player.vimeo.com/video/' +
        id +
        '?autoplay=1&byline=0&portrait=0&title=0';
    }

    if (!src) return;

    var iframe = document.createElement('iframe');
    iframe.src = src;
    iframe.allow =
      'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
    iframe.allowFullscreen = true;
    iframe.title = 'Video player';

    wrapper.appendChild(iframe);
    wrapper.removeAttribute('hidden');

    // Hide thumbnail and play button once iframe is live
    var coverImg = media.querySelector('.video-grid-cover-img');
    if (coverImg) coverImg.style.display = 'none';
    var playBtnEl = media.querySelector('.video-grid-play-btn');
    if (playBtnEl) playBtnEl.style.display = 'none';
  }

  /* -------------------------------------------------- */
  /* Bootstrap                                           */
  /* -------------------------------------------------- */

  function initVideoGrid(section) {
    upgradeAllThumbnails(section);
    initNativePlayers(section);
    initEmbedPlayers(section);
  }

  function init() {
    document
      .querySelectorAll('[data-section-type="video-grid"]')
      .forEach(function (section) {
        initVideoGrid(section);
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init when Shopify theme editor loads/reloads a section
  document.addEventListener('shopify:section:load', function (event) {
    var section = event.target.querySelector('[data-section-type="video-grid"]');
    if (section) initVideoGrid(section);
  });
})();
